import React from "react";
import { BookOpen, CheckCircle } from "lucide-react";
import clsx from "clsx";
import * as analytics from "@/services/analytics";

import { type Highlight } from "@/types/evidences";
import { useCurrentPdf } from "@/views/chat/hooks/useCurrentPdf";
import { CitationInfo } from "@/views/chat/types";

interface CitationButtonProps {
  citation: number | string;
  highlights: Highlight[];
  fseId: string;
  documentId: string;
  index: number;
}

export const CitationButton: React.FC<CitationButtonProps> = ({ citation, highlights, fseId, documentId, index }) => {
  const { handleCitationClick } = useCurrentPdf();
  const [isClicked, setIsClicked] = React.useState(false);

  const citationId = `${fseId}-${documentId}-${citation}-${index}`;

  const handleClick = React.useCallback(() => {
    analytics.evidenceShow();
    const highlight = highlights[0];
    const citationInfo: CitationInfo = {
      citationId,
      page: highlight.page,
      boundingRect: {
        left: highlight.min_x * 100,
        top: highlight.min_y * 100,
        width: (highlight.max_x - highlight.min_x) * 100,
        height: (highlight.max_y - highlight.min_y) * 100,
      },
      fseId,
      documentId,
      citation,
      index,
    };
    handleCitationClick(citationInfo);
    setIsClicked(true);
  }, [citationId, citation, documentId, fseId, handleCitationClick, highlights, index]);

  if (!highlights.length) {
    return null;
  }

  const buttonClasses = clsx(
    "mx-0.5 inline-flex items-center justify-center rounded border border-solid px-1 py-0.5 text-xs font-medium transition-colors duration-200",
    {
      "border-brand-gold-500 bg-brand-gold-100 text-brand-gold-800 hover:bg-brand-gold-200": isClicked,
      "border-midnight-blue-900 bg-midnight-blue-900 text-white hover:bg-midnight-blue-800": !isClicked,
    },
  );

  return (
    <button type="button" onClick={handleClick} className={buttonClasses}>
      {isClicked ? (
        <CheckCircle size={12} className="mr-0.5 text-brand-gold-800" />
      ) : (
        <BookOpen size={12} className="mr-0.5 text-white" />
      )}
      <span>p.{highlights[0].page + 1}</span>
    </button>
  );
};
